import React, { useState, useEffect, useMemo } from "react";
import { db, auth } from "../../firebase"; 
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import ContractCard from "./ContractCard";
import ConfirmModal from "../ConfirmModal";

export default function ContractBrowser({ userData, mode }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");
  const [filterType, setFilterType] = useState("All");

  const [openFilter, setOpenFilter] = useState(false);
  const [openSort, setOpenSort] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const closeMenus = () => {
      setOpenFilter(false);
      setOpenSort(false);
    };
    window.addEventListener("click", closeMenus);
    return () => window.removeEventListener("click", closeMenus);
  }, []);

  useEffect(() => {
    if (!userData?.userDisplayName) return;

    setLoading(true);
    const contractsRef = collection(db, "Contracts");
    let q;

    if (mode === "Public Contracts") {
      q = query(
        contractsRef, 
        where("recipient", "==", "Public"), 
        where("status", "==", "Outstanding"), 
        orderBy("createdAt", "desc")
      );
    } else if (mode === "Private Offers") {
      q = query(
        contractsRef, 
        where("recipient", "==", userData.userDisplayName), 
        where("status", "==", "Outstanding"), 
        orderBy("createdAt", "desc")
      );
    } else if (mode === "Active Contracts") {
      q = query(
        contractsRef,
        where("recipient", "==", userData.userDisplayName),
        where("status", "==", "Accepted"),
        orderBy("createdAt", "desc")
      );
    } else if (mode === "My Portfolio") {
      q = query(
        contractsRef, 
        where("issuer", "==", userData.userDisplayName), 
        orderBy("createdAt", "desc")
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setContracts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("Firestore Error:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [mode, userData]);

  const handleAcceptContract = async () => {
    if (!selectedContract) return;
    setIsProcessing(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch("https://acceptcontract-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify({ contractId: selectedContract.id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to accept contract");
      setIsModalOpen(false);
      setSelectedContract(null);
    } catch (err) {
      console.error("Acceptance Error:", err);
      alert(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const processedContracts = useMemo(() => {
    let result = [...contracts];
    if (filterType !== "All") result = result.filter(c => c.type === filterType);
    result.sort((a, b) => {
      if (sortBy === "newest") return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
      if (sortBy === "value") {
        const valA = a.amount || a.amount1 || 0;
        const valB = b.amount || b.amount1 || 0;
        return valB - valA;
      }
      if (sortBy === "expiry") {
        const expA = a.expiry || Infinity;
        const expB = b.expiry || Infinity;
        return expA - expB;
      }
      return 0;
    });
    return result;
  }, [contracts, sortBy, filterType]);

  const sortOptions = [
    { label: "NEWEST", value: "newest" },
    { label: "HIGHEST VALUE", value: "value" },
    { label: "EXPIRES SOONEST", value: "expiry" }
  ];

  if (loading) return (
    <div style={{ padding: "40px", textAlign: "center", color: "#50e3c2", fontFamily: "'JetBrains Mono', monospace", letterSpacing: "2px" }}>
      LOADING...
    </div>
  );

  return (
    <div className="browser-container" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#fff" }}>
      
      {/* --- CONFIRMATION MODAL --- */}
      {isModalOpen && (
        <ConfirmModal
          title="SYSTEM AUTHORIZATION"
          message={`Execute ${selectedContract?.type} terms with ${selectedContract?.issuer}? Assets will be moved from escrow.`}
          confirmText={isProcessing ? "PROCESSING..." : "EXECUTE"}
          cancelText="ABORT"
          onConfirm={handleAcceptContract}
          onCancel={() => !isProcessing && setIsModalOpen(false)}
          isProcessing={isProcessing}
        />
      )}

      {/* --- FILTER/SORT TOOLBAR (Custom Dropdowns) --- */}
      <div className="filter-bar" style={{ 
        display: "grid", 
        gridTemplateColumns: "1fr 1fr", 
        gap: "2px", 
        background: "rgba(80, 227, 194, 0.05)",
        padding: "4px",
        marginBottom: "12px",
        border: "1px solid rgba(80, 227, 194, 0.1)",
        position: "relative"
      }}>
        
        {/* CUSTOM FILTER */}
        <div 
          onClick={(e) => { e.stopPropagation(); setOpenFilter(!openFilter); setOpenSort(false); }}
          style={{ 
            display: "flex", flexDirection: "column", background: "rgba(0,0,0,0.3)", 
            padding: "6px 10px", borderLeft: "2px solid #50e3c2", cursor: "pointer", position: "relative" 
          }}
        >
          <label style={{ color: "#666", fontSize: "0.5rem", marginBottom: "4px" }}>FILTER TYPE</label>
          <div style={{ color: "#50e3c2", fontSize: "0.75rem", display: "flex", justifyContent: "space-between" }}>
            {filterType.toUpperCase()} <span>{openFilter ? "▴" : "▾"}</span>
          </div>
          
          {openFilter && (
            <div style={{ 
              position: "absolute", top: "100%", left: 0, width: "100%", zIndex: 3000, 
              background: "#051414", border: "1px solid #50e3c2", marginTop: "2px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.8)"
            }}>
              {["All", "Loan", "Exchange", "Option"].map(opt => (
                <div 
                  key={opt} 
                  onClick={() => setFilterType(opt)}
                  style={{ padding: "10px", color: "#50e3c2", fontSize: "0.7rem", borderBottom: "1px solid rgba(80, 227, 194, 0.05)" }}
                  onMouseEnter={(e) => e.target.style.background = "rgba(80, 227, 194, 0.1)"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  {opt.toUpperCase()}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CUSTOM SORT */}
        <div 
          onClick={(e) => { e.stopPropagation(); setOpenSort(!openSort); setOpenFilter(false); }}
          style={{ 
            display: "flex", flexDirection: "column", background: "rgba(0,0,0,0.3)", 
            padding: "6px 10px", borderLeft: "2px solid #50e3c2", cursor: "pointer", position: "relative" 
          }}
        >
          <label style={{ color: "#666", fontSize: "0.5rem", marginBottom: "4px" }}>SORT ORDER</label>
          <div style={{ color: "#50e3c2", fontSize: "0.75rem", display: "flex", justifyContent: "space-between" }}>
            {sortOptions.find(o => o.value === sortBy).label} <span>{openSort ? "▴" : "▾"}</span>
          </div>
          
          {openSort && (
            <div style={{ 
              position: "absolute", top: "100%", left: 0, width: "100%", zIndex: 3000, 
              background: "#051414", border: "1px solid #50e3c2", marginTop: "2px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.8)"
            }}>
              {sortOptions.map(opt => (
                <div 
                  key={opt.value} 
                  onClick={() => setSortBy(opt.value)}
                  style={{ padding: "10px", color: "#50e3c2", fontSize: "0.7rem", borderBottom: "1px solid rgba(244, 197, 66, 0.05)" }}
                  onMouseEnter={(e) => e.target.style.background = "rgba(244, 197, 66, 0.1)"}
                  onMouseLeave={(e) => e.target.style.background = "transparent"}
                >
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* --- CONTRACTS LIST --- */}
      <div className="contracts-list" style={{ 
  display: "flex", 
  flexDirection: "column", 
  gap: "10px",
  flex: 1,
  overflowY: "auto",
  paddingRight: "6px",
  scrollbarWidth: "thin",
  scrollbarColor: "#50e3c2 transparent"
}}>
        {processedContracts.length === 0 ? (
          <div style={{ 
            padding: "40px", textAlign: "center", border: "1px dashed #222", 
            color: "#444", fontSize: "0.8rem" 
          }}>
            NO CONTRACTS FOUND
          </div>
        ) : (
          processedContracts.map(c => (
            <ContractCard 
              key={c.id} 
              contract={c} 
              currentUser={userData} 
              onAccept={c.status === "Outstanding" ? () => {
                setSelectedContract(c);
                setIsModalOpen(true);
              } : null} 
            />
          ))
        )}
      </div>
    </div>
  );
}